// Recipes, costs, production batches, production reports and the catalogue's field-ownership
// review for the Admin (M5, WP5.2): URL search params, API calls and TanStack Query options.
// Every response is parsed with the shared @enp/domain/api schema (lib/api.ts).

import {
  type BatchCreateRequest,
  type CatalogueProductUpdateRequest,
  type RecipeDraftInput,
  batchDetailSchema,
  batchListResponseSchema,
  catalogueProductDetailSchema,
  catalogueProductListResponseSchema,
  catalogueReviewItemListResponseSchema,
  catalogueReviewItemSchema,
  costRollupResponseSchema,
  importComponentTargetsResponseSchema,
  productCostSchema,
  recipeDetailSchema,
  recipeListResponseSchema,
  stationsResponseSchema,
  usageReportSchema,
  yieldVarianceReportSchema,
} from '@enp/domain/api';
import { batchStatusSchema, type BatchStatus } from '@enp/domain/production';
import { isPlainDate } from '@enp/domain/time';
import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  type QueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';
import { catalogueKeys } from '../catalogue/queries.js';
import { api } from '../lib/api.js';
import { clean } from '../stock/queries.js';

const plainDate = z.string().refine(isPlainDate);
const optionalDay = plainDate.optional().catch(undefined);

/** Rows per page of the recipe and batch lists. */
export const PAGE_SIZE = 50;

export const productionKeys = {
  all: ['production'] as const,
  recipes: (search: RecipesSearch) => ['production', 'recipes', clean(search)] as const,
  recipe: (id: string) => ['production', 'recipe', id] as const,
  cost: (productId: string) => ['production', 'cost', productId] as const,
  batches: (search: BatchesQuery) => ['production', 'batches', clean(search)] as const,
  batch: (id: string) => ['production', 'batch', id] as const,
  yieldReport: (r: ReportQuery) => ['production', 'report', 'yield', clean(r)] as const,
  usageReport: (r: ReportQuery) => ['production', 'report', 'usage', clean(r)] as const,
  components: (q: string, exclude: string) => ['production', 'components', q, exclude] as const,
  stations: () => ['production', 'stations'] as const,
  reviewItems: (status: 'open' | 'resolved') => ['production', 'review-items', status] as const,
};

/** After a recipe, cost or batch change: what the production screens show is read again. */
export const invalidateProduction = (queryClient: QueryClient) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: productionKeys.all }),
    // Recipes and costs show on the catalogue's product page too.
    queryClient.invalidateQueries({ queryKey: catalogueKeys.all }),
  ]);

// ---------------------------------------------------------------- recipes

/** The recipe list's state in the URL. */
export const recipesSearchSchema = z.object({
  q: z.string().trim().min(1).max(100).optional().catch(undefined),
  /**
   * `draft`: a draft waits for sign-off (a change to a recipe in use too); `never_signed`: only
   * drafts; `active`: in use; `none`: only retired versions.
   */
  state: z.enum(['active', 'draft', 'never_signed', 'none']).optional().catch(undefined),
});
export type RecipesSearch = z.infer<typeof recipesSearchSchema>;

export const recipesQuery = (search: RecipesSearch) =>
  infiniteQueryOptions({
    queryKey: productionKeys.recipes(search),
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams();
      if (search.q) params.set('q', search.q);
      if (search.state) params.set('state', search.state);
      params.set('limit', String(PAGE_SIZE));
      if (pageParam) params.set('cursor', pageParam);
      return api(`/recipes?${params}`, { signal, schema: recipeListResponseSchema });
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    retry: false,
  });

export const recipeQuery = (id: string) =>
  queryOptions({
    queryKey: productionKeys.recipe(id),
    queryFn: ({ signal }) =>
      api(`/recipes/${encodeURIComponent(id)}`, { signal, schema: recipeDetailSchema }),
    retry: false,
  });

/**
 * Admin creates are retry-safe (D-063, D-065): each form sends one key, made when it opens;
 * a repeat with the key (a double click, a retry) gets the first answer and makes nothing.
 */
const once = (idempotencyKey: string | undefined) =>
  idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {};

export const createRecipe = (productId: string, draft: RecipeDraftInput, idempotencyKey?: string) =>
  api('/recipes', {
    method: 'POST',
    body: { product_id: productId, draft },
    schema: recipeDetailSchema,
    ...once(idempotencyKey),
  });

/** A new draft: a copy of `fromVersionId` (default: the latest version). */
export const createVersion = (recipeId: string, fromVersionId?: string, idempotencyKey?: string) =>
  api(`/recipes/${encodeURIComponent(recipeId)}/versions`, {
    method: 'POST',
    body: fromVersionId ? { from_version_id: fromVersionId } : {},
    schema: recipeDetailSchema,
    ...once(idempotencyKey),
  });

export const updateDraft = (recipeId: string, versionId: string, draft: RecipeDraftInput) =>
  api(`/recipes/${encodeURIComponent(recipeId)}/versions/${encodeURIComponent(versionId)}`, {
    method: 'PUT',
    body: draft,
    schema: recipeDetailSchema,
  });

/** The baker signs off; `exactLineIds`: warned lines they confirmed exact (UI5A-14). */
export const signOffVersion = (
  recipeId: string,
  versionId: string,
  statement: string,
  exactLineIds: readonly string[] = [],
) =>
  api(
    `/recipes/${encodeURIComponent(recipeId)}/versions/${encodeURIComponent(versionId)}/sign-off`,
    {
      method: 'POST',
      body: {
        allergen_statement: statement,
        ...(exactLineIds.length > 0 ? { exact_line_ids: [...exactLineIds] } : {}),
      },
      schema: recipeDetailSchema,
    },
  );

export const retireVersion = (recipeId: string, versionId: string, reason?: string) =>
  api(`/recipes/${encodeURIComponent(recipeId)}/versions/${encodeURIComponent(versionId)}/retire`, {
    method: 'POST',
    body: reason ? { reason } : {},
    schema: recipeDetailSchema,
  });

/**
 * Products a recipe can use, by name or code (the editor's picker). Every live product: a
 * recipe may use an ingredient, a sub-recipe, packaging, a supply or something sold (a cake
 * cut into slices).
 */
export const componentSearchQuery = (q: string, excludeId: string) =>
  queryOptions({
    queryKey: productionKeys.components(q, excludeId),
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({ q, limit: '30' });
      const res = await api(`/catalogue/products?${params}`, {
        signal,
        schema: catalogueProductListResponseSchema,
      });
      return res.items.filter((p) => p.id !== excludeId && !p.roles.includes('service'));
    },
    enabled: q.trim().length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: false,
  });

// ---------------------------------------------------------------- costs

export const productCostQuery = (productId: string) =>
  queryOptions({
    queryKey: productionKeys.cost(productId),
    queryFn: ({ signal }) =>
      api(`/costs/products/${encodeURIComponent(productId)}`, {
        signal,
        schema: productCostSchema,
      }),
    staleTime: 30_000,
    retry: false,
  });

/** Recomputes (and stores) standard costs: every live product, or these. */
export const runCostRollup = (productIds?: string[]) =>
  api('/costs/rollup', {
    method: 'POST',
    body: productIds ? { product_ids: productIds } : {},
    schema: costRollupResponseSchema,
  });

/** The cost screen's state in the URL: the product search. */
export const costsSearchSchema = z.object({
  q: z.string().trim().min(1).max(100).optional().catch(undefined),
});
export type CostsSearch = z.infer<typeof costsSearchSchema>;

/** Made products (they have a cost to roll up), by name: the cost screen's search. */
export const madeProductsQuery = (q: string | undefined) =>
  queryOptions({
    queryKey: ['production', 'made-products', q ?? ''] as const,
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ limit: '50' });
      if (q) params.set('q', q);
      return api(`/catalogue/products?${params}`, {
        signal,
        schema: catalogueProductListResponseSchema,
      });
    },
    placeholderData: keepPreviousData,
    retry: false,
  });

// ---------------------------------------------------------------- batches

/** Statuses the batch list can filter by. */
export const BATCH_STATUS_FILTERS = ['planned', 'started', 'completed', 'cancelled'] as const;

/** The batch list's state in the URL: one local day, a branch, a status. */
export const batchesSearchSchema = z.object({
  day: optionalDay,
  branch: z.uuid().optional().catch(undefined),
  status: batchStatusSchema.optional().catch(undefined),
  product: z.uuid().optional().catch(undefined),
});
export type BatchesSearch = z.infer<typeof batchesSearchSchema>;

/** What the list asks the API for (the day resolved to today when absent). */
export interface BatchesQuery {
  day: string;
  branch?: string;
  status?: BatchStatus;
  product?: string;
}

export const batchesQuery = (q: BatchesQuery) =>
  infiniteQueryOptions({
    queryKey: productionKeys.batches(q),
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams({ day: q.day, limit: String(PAGE_SIZE) });
      if (q.branch) params.set('branch_id', q.branch);
      if (q.status) params.set('status', q.status);
      if (q.product) params.set('product_id', q.product);
      if (pageParam) params.set('cursor', pageParam);
      return api(`/production/batches?${params}`, { signal, schema: batchListResponseSchema });
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    retry: false,
  });

export const batchQuery = (id: string) =>
  queryOptions({
    queryKey: productionKeys.batch(id),
    queryFn: ({ signal }) =>
      api(`/production/batches/${encodeURIComponent(id)}`, { signal, schema: batchDetailSchema }),
    retry: false,
  });

export const createBatch = (body: BatchCreateRequest, idempotencyKey?: string) =>
  api('/production/batches', {
    method: 'POST',
    body,
    schema: batchDetailSchema,
    ...once(idempotencyKey),
  });

export const cancelBatch = (id: string, reason: string) =>
  api(`/production/batches/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    body: { reason },
    schema: batchDetailSchema,
  });

/** The account's stations (a batch may be put on one). */
export const stationsQuery = () =>
  queryOptions({
    queryKey: productionKeys.stations(),
    queryFn: ({ signal }) => api('/stations', { signal, schema: stationsResponseSchema }),
    staleTime: 5 * 60_000,
    retry: false,
  });

// ---------------------------------------------------------------- reports

/** The reports' state in the URL: which report, the period, a product, a branch. */
export const reportsSearchSchema = z.object({
  report: z.enum(['yield', 'usage']).optional().catch(undefined),
  from: optionalDay,
  to: optionalDay,
  product: z.uuid().optional().catch(undefined),
  branch: z.uuid().optional().catch(undefined),
});
export type ReportsSearch = z.infer<typeof reportsSearchSchema>;

export interface ReportQuery {
  from: string;
  to: string;
  product?: string;
  branch?: string;
}

const reportParams = (r: ReportQuery) => {
  const params = new URLSearchParams({ from: r.from, to: r.to });
  if (r.product) params.set('product_id', r.product);
  if (r.branch) params.set('branch_id', r.branch);
  return params;
};

export const yieldReportQuery = (r: ReportQuery) =>
  queryOptions({
    queryKey: productionKeys.yieldReport(r),
    queryFn: ({ signal }) =>
      api(`/production/reports/yield-variance?${reportParams(r)}`, {
        signal,
        schema: yieldVarianceReportSchema,
      }),
    placeholderData: keepPreviousData,
    retry: false,
  });

export const usageReportQuery = (r: ReportQuery) =>
  queryOptions({
    queryKey: productionKeys.usageReport(r),
    queryFn: ({ signal }) =>
      api(`/production/reports/usage?${reportParams(r)}`, { signal, schema: usageReportSchema }),
    placeholderData: keepPreviousData,
    retry: false,
  });

// ---------------------------------------------------------------- catalogue edits

export const updateProduct = (id: string, body: CatalogueProductUpdateRequest) =>
  api(`/catalogue/products/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
    schema: catalogueProductDetailSchema,
  });

/** The review screen's state in the URL. */
export const reviewSearchSchema = z.object({
  status: z.enum(['resolved']).optional().catch(undefined),
});

export const reviewItemsQuery = (status: 'open' | 'resolved') =>
  infiniteQueryOptions({
    queryKey: productionKeys.reviewItems(status),
    queryFn: ({ pageParam, signal }) => {
      const params = new URLSearchParams({ status, limit: String(PAGE_SIZE) });
      if (pageParam) params.set('cursor', pageParam);
      return api(`/catalogue/review-items?${params}`, {
        signal,
        schema: catalogueReviewItemListResponseSchema,
      });
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    retry: false,
  });

export const resolveReviewItem = (id: string, resolution: 'keep' | 'take') =>
  api(`/catalogue/review-items/${encodeURIComponent(id)}/resolve`, {
    method: 'POST',
    body: { resolution },
    schema: catalogueReviewItemSchema,
  });

// ---------------------------------------------------------------- imports (B-024)

/** Records of an import run that recipes can use instead of an unmatched component. */
export const componentTargetsQuery = (runId: string, q: string) =>
  queryOptions({
    queryKey: ['imports', 'component-targets', runId, q] as const,
    queryFn: ({ signal }) =>
      api(
        `/imports/runs/${encodeURIComponent(runId)}/component-targets?q=${encodeURIComponent(q)}&limit=50`,
        { signal, schema: importComponentTargetsResponseSchema },
      ),
    enabled: q.trim().length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: false,
  });
